import React, { FC, useCallback, useContext, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Col } from 'reactstrap';
import { CashierBadgeData } from '../../../../shared/user/cashier';
import { HotkeyButton } from '../../../components';
import { BadgePageRender, transformNodeMap } from '../../../modules/badgerenderer/BadgeRender';
import { store } from '../../../services';
import { TranslationProvider, useTranslation } from '../../../translations';
import { captureError } from '../../../utils/errorHandling';
import { ReprintBadgeModal } from '../ReprintBadge';
import { CashierContext } from '../cashiercontext';

interface PrinterButtonProps {
  readonly hotkey: string;
  readonly badge: CashierBadgeData;
  readonly isReprint?: boolean;
  readonly requireExtraConfirmation?: boolean;
  readonly outline?: boolean;
}

export const OSPrinterButton: FC<PrinterButtonProps> = ({
  badge,
  outline,
  hotkey,
  isReprint,
  requireExtraConfirmation,
}) => {
  const { ts } = useTranslation();
  const cashierSettings = useContext(CashierContext);
  const [design, setDesign] = useState(cashierSettings.config.overrideDesign ?? badge.design);
  const [rendering, setRendering] = useState(false);

  const [reprintReasonModalState, setReprintReasonModalState] = useState(false);
  const [reprintConfirmed, setReprintConfirmed] = useState(false);

  const actualDesign = cashierSettings.config.overrideDesign ?? design;

  const handlePrint = useCallback(
    (reason?: string) => {
      // It is a re-print, a reason is not given, and a reason is required
      if (!reason && isReprint && !cashierSettings.config.showReprintModal) {
        setReprintReasonModalState(true);
        return;
      }

      if (badge.labels[0]!.userId) {
        const barcode = new URLSearchParams(location.search).get('barcode') ?? undefined;
        api
          .markRegistrationBadgeAsPrinted(badge.labels[0]!.userId, null, barcode, reason)
          .catch(captureError);
      }

      setRendering(true);
      const root = createRoot(document.getElementById('printArea')!);
      // TODO: Still need to print the extra labels
      root.render(
        <Provider store={store}>
          <TranslationProvider>
            <BadgePageRender labels={badge.labels} nodes={transformNodeMap(design)} />
          </TranslationProvider>
        </Provider>,
      );

      setTimeout(() => {
        window.print();

        root.render(<div />);
        setRendering(false);
      }, 200);
    },
    [badge.labels, cashierSettings.config.showReprintModal, design, isReprint],
  );

  return (
    <Col lg={12}>
      {isReprint && reprintReasonModalState && (
        <ReprintBadgeModal
          close={() => {
            setReprintReasonModalState(false);
          }}
          currentDesign={actualDesign}
          save={(reason) => {
            setReprintReasonModalState(false);
            setReprintConfirmed(true);
            handlePrint(reason);
          }}
          setDesign={setDesign}
        />
      )}
      {(!isReprint || !cashierSettings.config.showReprintModal || reprintConfirmed) && (
        <HotkeyButton
          actionText="print_badge"
          block
          color="primary"
          disabled={rendering}
          hotkey={hotkey}
          id="printBadge"
          onClick={handlePrint}
          outline={outline}
          requiredKeyPressCount={requireExtraConfirmation ? 3 : 1}
        >
          {isReprint ? ts('reprint_badge') : ts('print_badge_with_type', 'Badge')}
        </HotkeyButton>
      )}
    </Col>
  );
};
